<template>
    <page-loading v-if="loading"></page-loading>
    <form-container v-else>
        <step-form-heading v-if="!props.groupCollectionId"
            :icon="faRectangleHistoryCirclePlus">
            New Group Collection

            <template v-slot:description>
                Create a reusable filter for Groups.
            </template>
        </step-form-heading>

        <page-header v-else
            class="vx-mb-6 lg:vx-mb-12">
            <template v-slot:subtitle>
                <a :href="`/settings/teams/${teamId}#/group-collections`"
                    class="!vx-no-underline !vx-text-sky-600 vx-text-base vx-font-normal">
                    <font-awesome-icon class="vx-mr-1"
                        :icon="faArrowLeft"></font-awesome-icon>
                    All Group Collections
                </a>
            </template>

            {{ groupCollection.name }}
        </page-header>

        <div :class="{ 'vx-grid vx-grid-cols-12': props.groupCollectionId }">
            <page-sidebar v-if="props.groupCollectionId"
                appearance="nested"
                :links="[
                    { href: `/settings/teams/${teamId}/group-collections/${props.groupCollectionId}`, label: 'Details' },
                ]"></page-sidebar>
            <vx-sticky-nav :sections="[
                { id: 'name', label: 'Name' },
                { id: 'description', label: 'Description' },
                { id: 'filters', label: 'Groups' },
            ]"
                class="vx-col-span-12"
                :class="{ 'lg:vx-col-span-10': !!props.groupCollectionId }"
                :navClasses="`${success || props.groupCollectionId ? 'vx-hidden' : ''}`">

                <div class="vx-flex vx-flex-col vx-w-full vx-justify-center vx-gap-2 md:vx-gap-8 vx-mx-auto">

                    <vx-alert v-if="success"
                        color="success"
                        class="vx-max-w-screen-sm vx-w-full"
                        :class="{ 'vx-mx-auto': !props.groupCollectionId }"
                        :closeable="false"
                        shadow>
                        <template v-slot:icon>
                            <font-awesome-icon :icon="faCircleCheck"></font-awesome-icon>
                        </template>
                        <div class="vx-flex vx-items-center vx-justify-between">
                            <span>
                                <strong>{{ props.groupCollectionId ? 'Existing Group Collection' : 'New Group Collection' }}</strong>
                                {{ props.groupCollectionId ? 'updated' : 'created' }} successfully
                            </span>
                            <vx-button :href="`/settings/teams/${teamId}#/group-collections`"
                                size="sm"
                                color="success">Back to
                                Group Collections<font-awesome-icon
                                    :icon="faSquareArrowUpLeft"></font-awesome-icon></vx-button>
                        </div>
                    </vx-alert>

                    <vx-alert v-if="error"
                        color="danger"
                        class="vx-max-w-screen-sm vx-w-full"
                        :class="{ 'vx-mx-auto': !props.groupCollectionId }"
                        :closeable="false"
                        shadow>{{ error }}</vx-alert>

                    <vx-expandable-card id="name"
                        class="vx-max-w-screen-sm vx-w-full"
                        :class="{ 'vx-mx-auto': !props.groupCollectionId }">
                        <template v-slot:header>
                            Name
                        </template>
                        <template v-slot:content>
                            <vx-input data-test="name"
                                autofocus
                                v-model="groupCollection.name"
                                size="lg"
                                :error="displayFirstError(validation.name)"></vx-input>
                        </template>
                    </vx-expandable-card>

                    <vx-expandable-card id="description"
                        class="vx-max-w-screen-sm vx-w-full"
                        :class="{ 'vx-mx-auto': !props.groupCollectionId }">
                        <template v-slot:header>
                           Description
                        </template>
                        <template v-slot:content>
                            <vx-input data-test="description"
                                v-model="groupCollection.description"
                                size="lg"
                                :rows="4"
                                :error="displayFirstError(validation.description)"></vx-input>
                        </template>
                    </vx-expandable-card>

                    <vx-expandable-card id="filters"
                        :class="{
                            'vx-mx-auto vx-max-w-5xl vx-w-full': !props.groupCollectionId,
                            'vx-w-full xl:vx-w-[62rem]': props.groupCollectionId
                        }">
                        <template v-slot:header>
                            Group Filters
                        </template>

                        <template v-slot:content>
                            <group-collection-filters
                                v-model="groupCollection.group_filters"></group-collection-filters>
                        </template>
                    </vx-expandable-card>

                    <vx-sticky-footer class="vx-max-w-screen-sm vx-w-full"
                        :class="{ 'vx-mx-auto': !props.groupCollectionId }">
                        <vx-button :href="`/settings/teams/${teamId}#/group-collections`"
                            class="vx-w-20"
                            size="lg"
                            color="muted">
                            Cancel
                        </vx-button>
                        <vx-button @click="submit()"
                            data-test="submit-button"
                            class="vx-grow"
                            size="lg"
                            color="primary"
                            :loading="submitting">
                            {{
                                props.groupCollectionId
                                    ? 'Update'
                                    : 'Save'
                            }}
                        </vx-button>
                    </vx-sticky-footer>
                </div>
            </vx-sticky-nav>
        </div>
    </form-container>
</template>

<script setup>
import StepFormHeading from '~/components/general/StepFormHeading.vue';
import PageLoading from '~/components/general/PageLoading.vue';
import PageHeader from '~/components/general/PageHeader.vue';
import PageSidebar from '~/components/general/PageSidebar.vue';
import FormContainer from '~/components/general/FormContainer.vue';
import {
    VxButton,
    VxAlert
} from "@voxie/frontend-components";
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faCircleCheck, faSquareArrowUpLeft, faArrowLeft, faRectangleHistoryCirclePlus } from '@fortawesome/pro-solid-svg-icons';
import { ref, onMounted } from 'vue';
import { VxStickyNav, VxStickyFooter, VxExpandableCard, VxInput } from '@voxie/frontend-components';
import { useToasted } from '~/composables/useToasted';
import useVuelidate from '@vuelidate/core';
import { required, maxLength, helpers } from '@vuelidate/validators';
import { displayFirstError } from '~/utils/validation';
import { HTTP_INTERNAL_SERVER_ERROR } from '~/support/HttpStatuses';
import groupCollectionsClient from '~/services/groupCollectionsClient';
import GroupCollectionFilters from './GroupCollectionFilters.vue';
import { randomString } from '~/utils/string';

const toasted = useToasted();
const teamId = Spark.state.currentTeam.id;

const props = defineProps({
    groupCollectionId: {
        type: [String, Number],
        required: false,
    },
});

const defaultData = () => {
    return {
        name: '',
        group_filters: [
            {
                id: randomString(),
                attribute: '',
                operator: '',
                key: '',
                values: [],
            }
        ],
    }
}
const groupCollection = ref(defaultData());
const loading = ref(false);

const error = ref();
const success = ref(false);
const submitting = ref(false);

onMounted(async () => {
    if (props.groupCollectionId) {
        loading.value = true;
        const response = await groupCollectionsClient.showGroupCollection(teamId, props.groupCollectionId);
        groupCollection.value = response.data;
        loading.value = false;
    }
});


const validation = useVuelidate({
    name: {
        required,
        maxLength: maxLength(80),
        format: helpers.withMessage('You can use only alphanumeric letters and the following symbols: _-', helpers.regex(/^[a-zA-Z0-9][a-zA-Z0-9 _-]*$/)),
    },
    description: {
        maxLength: maxLength(2000)
    }
}, groupCollection, { $autoDirty: true });

const submit = async () => {
    if (submitting.value) {
        return;
    }

    const isValid = await validation.value.$validate();

    if (!isValid) {
        toasted.error('Please check form data.');
        error.value = 'Please check form data.';
        success.value = false;
        return;
    }

    submitting.value = true;

    try {
        if (!props.groupCollectionId) {
            await groupCollectionsClient.createGroupCollection(teamId, groupCollection.value);
            groupCollection.value = defaultData();
        } else {
            await groupCollectionsClient.updateGroupCollection(teamId, props.groupCollectionId, groupCollection.value);
        }

        success.value = true;
        error.value = null;
        validation.value.$reset();
    } catch (e) {
        if (!e?.response?.status || e.response.status >= HTTP_INTERNAL_SERVER_ERROR) {
            error.value = 'Something went wrong.'
        } else {
            error.value = e.response?.data?.message || 'Something went wrong.';
        }
        toasted.error(error.value);

        success.value = false;
    } finally {
        submitting.value = false;
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }
};

</script>
